import { ISiderMenu, useAuthContext } from '@contexts/AuthProvider';
import { AuthService } from '@core/services/auth.service';
import { CustomService } from '@core/services/custom.service';
import { Spin } from 'antd';
import React, { ComponentType, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import { insertColon } from './utils';

export function withCheckAccessTime<P>(
  Component: ComponentType<P>
): React.FC<P> {
  return function withCheckAccessTime(props: P): JSX.Element {
    const [canAccess, setCanAccess] = useState<boolean>();
    // check-access could not be answered at all - a forged menu id, or a transient failure.
    // Distinct from canAccess === false, which means the menu exists but is outside its hours.
    const [denied, setDenied] = useState(false);
    const {
      defaultMenu: { menuId, subMenuId },
    } = useAuthContext();
    const sideMenuStorage = AuthService.getSideMenus() || '';

    const siderMenus: ISiderMenu = sideMenuStorage
      ? JSON.parse(sideMenuStorage)
      : undefined;

    useEffect(() => {
      let mounted = true;
      if (!mounted) return;
      callAccessTime();
      return () => {
        mounted = false;
      };
    }, [subMenuId]);

    const callAccessTime = () => {
      CustomService.getData<boolean>(
        '/users/menu/check-access',
        undefined
      ).subscribe({
        next: (response: boolean) => {
          setCanAccess(response);
        },
        // Without this the screen hangs on a spinner forever. canAccess stays undefined, and the
        // render below returns AcessTimeLoading for exactly that value, so there is no state the
        // component can ever reach again.
        //
        // Reachable by forging the menuId in the URL: check-access looks the menu up by id and answers
        // 400 "Not found menu id 123123" for one that does not exist. withMenuAccess cannot catch it -
        // that validates the route PATH against the sidebar, and a tampered URL keeps a path the user
        // legitimately holds while changing only the id. Confirmed in practice on the sibling app.
        //
        // Sent home rather than to /error/access, which is for a menu that exists but is outside its
        // operating hours: this request could not be validated at all, so there are no hours to show.
        // A transient failure lands here too, and home is right for that as well - this runs at
        // navigation time, so the cost is clicking the menu again, against a page that never loads.
        error: () => {
          setDenied(true);
        },
      });
    };

    const getNotifyAccessTime = (): {
      title: string;
      accessTimes: string[];
    } => {
      const menu = siderMenus?.menus.find((m) => m.id === +menuId[0]);
      const subMenu = menu?.subMenus.find((s) => s.id === +subMenuId[0]);
      const accessTimes =
        subMenu?.times.map((time) => {
          return time
            ? `[ ${insertColon(time.from)}-${insertColon(time.to)} ]`
            : '';
        }) || [];

      return { title: 'common:permissionDeny', accessTimes };
    };

    if (denied) {
      // Redirect replaces the history entry, so Back does not drop them onto a URL that never loads
      return <Redirect to="/" />;
    }
    if (canAccess === undefined) {
      return <AcessTimeLoading size="large" />;
    }
    return canAccess ? (
      <Component {...props} />
    ) : (
      <Redirect
        to={{
          pathname: '/error/access',
          state: getNotifyAccessTime(),
        }}
      />
    );
  };
}

const AcessTimeLoading = styled(Spin)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
